import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Question from "../../components/Question";
import { Box, FormControl, Chip, Stack, useTheme, useMediaQuery } from "@mui/material";
import { setPatientHistory } from "../../redux/userSlice";
import ContinueButton from "../../components/ContinueButton";
import { continueButtonSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

const OPTIONS = [
  "Smoke",
  "Alcohol use",
  "Illicit drug use",
  "Contact lens use",
];

export default function PatientHistoryLifestyle() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const user = useSelector((state) => state.user);
  const [selected, setSelected] = useState([]);

  const labelToKey = useMemo(
    () => ({
      Smoke: "smoking",
      "Alcohol use": "alcohol",
      "Illicit drug use": "illicitDrugs",
      "Contact lens use": "contactLens",
    }),
    []
  );

  const keyToLabel = useMemo(
    () => Object.fromEntries(Object.entries(labelToKey).map(([label, key]) => [key, label])),
    [labelToKey]
  );

  useEffect(() => {
    if (user.patientHistory) {
      const savedYes = Object.entries(user.patientHistory)
        .filter(([_, value]) => value === "Y")
        .map(([key]) => keyToLabel[key] || null)
        .filter(Boolean);

      setSelected(savedYes);
    }
  }, [user.patientHistory, keyToLabel]);

  const toggle = (label) => {
    setSelected((prev) => (prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]));
  };

  const handleNext = () => {
    const prev = user.patientHistory || {};
    const updates = Object.fromEntries(
      OPTIONS.map((label) => {
        const key = labelToKey[label];
        return [key, selected.includes(label) ? "Y" : "N"];
      })
    );

    dispatch(setPatientHistory({ ...prev, ...updates }));
    setPageAndDeferNavigate(dispatch, "/duration", navigate);
  };

  const handleBack = () => {
    setPageAndDeferNavigate(dispatch, "/patient-history-medical", navigate);
  };

  return (
    <Question
      onBack={handleBack}
      title="Tell us about your lifestyle habits."
      subtitle="Select all that apply. Your answers help us personalize your care."
      input={
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <FormControl sx={{ width: "100%", mb: "1rem" }}>
            <Box sx={{ width: "100%", maxWidth: isDesktop ? "800px" : "100%", mx: "auto", mt: 2 }}>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {OPTIONS.map((label) => {
                  const isSelected = selected.includes(label);
                  return (
                    <Chip
                      key={label}
                      label={label}
                      clickable
                      onClick={() => toggle(label)}
                      variant={isSelected ? "filled" : "outlined"}
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: isSelected ? 600 : 400,
                        backgroundColor: isSelected ? "#82B1FF33" : "transparent",
                        color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                        borderColor: isSelected ? theme.palette.primary.main : theme.palette.grey[400],
                        height: "auto",
                        alignItems: "flex-start",
                        maxWidth: isDesktop ? "380px" : "100%",
                        "& .MuiChip-label": {
                          display: "block",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                          lineHeight: 1.2,
                          paddingTop: "6px",
                          paddingBottom: "6px",
                        },
                        "&:hover": {
                          backgroundColor: isSelected ? "#82B1FF33" : theme.palette.action.hover,
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          </FormControl>
        </Box>
      }
      footer={
        <Box sx={{ ...continueButtonSx }}>
          <ContinueButton onClick={handleNext} title="Continue" selected />
        </Box>
      }
    />
  );
}
